import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowRight, CheckCircle2, Target, Layers, TrendingUp } from 'lucide-react';

const About = () => {
  const fadeInUp = {
    initial: { opacity: 0, y: 30 },
    animate: { opacity: 1, y: 0 },
    transition: { duration: 0.6 }
  };

  const whatWeDo = [
    {
      icon: Target,
      title: 'Strategic Consultancy',
      description: 'Hands-on advisory for organisations managing complex project portfolios. We work alongside your teams to implement governance frameworks, optimise delivery processes, and build lasting capability.'
    },
    {
      icon: Layers,
      title: 'Videra Platform',
      description: 'Purpose-built portfolio management software that replaces spreadsheet chaos with real-time visibility. One platform for governance, reporting, and stakeholder confidence.'
    },
    {
      icon: TrendingUp,
      title: 'Transformation Support',
      description: 'From PMO setup to operating model design, we help organisations scale their project delivery capabilities without the growing pains.'
    },
  ];

  const outcomes = [
    'Clear visibility across your entire project portfolio',
    'Governance frameworks that actually get used',
    'Reporting that takes minutes, not days',
    'Stakeholder confidence in programme performance',
    'Scalable processes that grow with your organisation',
    'Reduced risk through structured delivery approaches'
  ];

  return (
    <div className="min-h-screen pt-20" data-testid="about-page">
      {/* Hero Section */}
      <section className="py-20 md:py-32">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div 
            className="max-w-3xl"
            initial="initial"
            animate="animate"
            variants={fadeInUp}
          >
            <p className="text-xs uppercase tracking-[0.2em] text-slate-500 font-semibold mb-6">
              About Us
            </p>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-medium tracking-tight text-slate-900 leading-[1.1]">
              Clarity for complex portfolios
            </h1>
            <p className="mt-8 text-lg lg:text-xl text-slate-600 leading-relaxed">
              Keystone Strategic Consultants helps organisations take control of 
              their project portfolios. Through hands-on consultancy and our 
              Videra platform, we bring structure, visibility, and confidence 
              to even the most demanding programmes.
            </p>
          </motion.div>
        </div>
      </section>

      {/* What We Do */}
      <section className="py-20 md:py-32 bg-slate-50 border-y border-slate-200" data-testid="what-we-do">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="max-w-3xl mb-16"
          >
            <p className="text-xs uppercase tracking-[0.2em] text-slate-500 font-semibold mb-4">
              What We Do
            </p>
            <h2 className="text-3xl md:text-4xl lg:text-5xl font-medium tracking-tight text-slate-900">
              Two ways to work with us
            </h2>
            <p className="mt-6 text-lg text-slate-600">
              Whether you need expert guidance, a powerful platform, or both — 
              we tailor our approach to your organisation's needs.
            </p>
          </motion.div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            {whatWeDo.map((item, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="bg-white p-10 border border-slate-200"
                data-testid={`service-card-${index}`}
              >
                <div className="w-14 h-14 bg-slate-900 flex items-center justify-center mb-8">
                  <item.icon className="w-7 h-7 text-white" />
                </div>
                <h3 className="text-xl font-medium text-slate-900 mb-4">{item.title}</h3>
                <p className="text-slate-600 leading-relaxed">{item.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Our Approach */}
      <section className="py-20 md:py-32" data-testid="approach-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
            >
              <p className="text-xs uppercase tracking-[0.2em] text-slate-500 font-semibold mb-4">
                Our Approach
              </p>
              <h2 className="text-3xl md:text-4xl font-medium tracking-tight text-slate-900">
                We embed, not advise from the sidelines
              </h2>
              <p className="mt-8 text-lg text-slate-600 leading-relaxed">
                Too many consultancies deliver a report and disappear. We take a 
                different approach — working alongside your teams to implement 
                real change that sticks.
              </p>
              <p className="mt-6 text-lg text-slate-600 leading-relaxed">
                Our frameworks are designed to become your frameworks. Our systems 
                become your competitive advantage. When we leave, you're stronger 
                than when we arrived.
              </p>
              <p className="mt-6 text-lg text-slate-600 leading-relaxed font-medium">
                That's what twenty years of delivery teaches you — what actually 
                works when the pressure is on.
              </p>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="bg-slate-900 p-10"
            >
              <p className="text-xs uppercase tracking-[0.15em] text-blue-400 font-semibold mb-6">
                Client Outcomes
              </p>
              <ul className="space-y-4">
                {outcomes.map((outcome, index) => (
                  <li key={index} className="flex items-start gap-4">
                    <CheckCircle2 className="w-5 h-5 text-blue-400 flex-shrink-0 mt-0.5" />
                    <span className="text-slate-200">{outcome}</span>
                  </li>
                ))}
              </ul>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Sectors */}
      <section className="py-20 md:py-32 bg-slate-50 border-y border-slate-200" data-testid="sectors-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center max-w-3xl mx-auto mb-16"
          >
            <p className="text-xs uppercase tracking-[0.2em] text-slate-500 font-semibold mb-4">
              Sectors
            </p>
            <h2 className="text-3xl md:text-4xl lg:text-5xl font-medium tracking-tight text-slate-900">
              Built for complex environments
            </h2>
            <p className="mt-6 text-lg text-slate-600">
              Our expertise is particularly relevant for organisations in 
              high-stakes, heavily regulated sectors where project governance 
              is critical.
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {[
              { title: 'Healthcare & NHS', desc: 'Acute trusts, community services, capital programmes' },
              { title: 'PFI & SPVs', desc: 'Lifecycle management, contract compliance, stakeholder reporting' },
              { title: 'Facilities Management', desc: 'Portfolio delivery, planned maintenance, capital works' },
              { title: 'Infrastructure', desc: 'Large-scale programmes, multi-site delivery, compliance' },
            ].map((sector, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="bg-white p-8 border border-slate-200"
                data-testid={`sector-${index}`}
              >
                <h3 className="text-lg font-medium text-slate-900 mb-2">{sector.title}</h3>
                <p className="text-slate-600 text-sm">{sector.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Videra Teaser */}
      <section className="py-20 md:py-32" data-testid="videra-teaser">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              whileInView={{ opacity: 1, scale: 1 }}
              viewport={{ once: true }}
            >
              <div className="bg-slate-100 rounded-xl p-2">
                <img
                  src="https://images.unsplash.com/photo-1763718528755-4bca23f82ac3?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzMzV8MHwxfHNlYXJjaHwzfHxkYXNoYm9hcmQlMjB1aSUyMG1vY2t1cHxlbnwwfHx8fDE3NzUzNDA5NDl8MA&ixlib=rb-4.1.0&q=85"
                  alt="Videra Dashboard"
                  className="w-full rounded-lg"
                />
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
            >
              <p className="text-xs uppercase tracking-[0.2em] text-slate-500 font-semibold mb-4">
                The Videra Platform
              </p>
              <h2 className="text-3xl md:text-4xl font-medium tracking-tight text-slate-900">
                Software that understands your world
              </h2>
              <p className="mt-8 text-lg text-slate-600 leading-relaxed">
                Videra wasn't built by software engineers guessing what project 
                managers need. It was built from direct experience of the 
                frustrations, workarounds, and gaps in existing tools.
              </p>
              <p className="mt-6 text-lg text-slate-600 leading-relaxed">
                The result? A platform that fits how you actually work — with 
                governance baked in, reporting automated, and stakeholder 
                visibility built from day one.
              </p>
              <Link
                to="/videra"
                className="inline-flex items-center mt-8 bg-slate-900 text-white px-8 py-4 font-medium tracking-wide hover:bg-slate-800 transition-all group"
                data-testid="videra-cta"
              >
                Explore Videra
                <ArrowRight className="ml-2 w-4 h-4 group-hover:translate-x-1 transition-transform" />
              </Link>
            </motion.div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 md:py-32 bg-slate-900" data-testid="about-cta">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="text-3xl md:text-4xl lg:text-5xl font-medium tracking-tight text-white">
              Let's talk about your portfolio
            </h2>
            <p className="mt-6 text-lg text-slate-300 max-w-2xl mx-auto">
              Every organisation's challenges are different. Get in touch to 
              discuss how we can help bring clarity to yours.
            </p>
            <Link
              to="/contact"
              data-testid="about-contact-cta"
              className="inline-flex items-center mt-10 bg-white text-slate-900 px-10 py-5 font-medium tracking-wide hover:bg-slate-100 transition-all group"
            >
              Start a Conversation
              <ArrowRight className="ml-2 w-4 h-4 group-hover:translate-x-1 transition-transform" />
            </Link>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default About;
